'use client';

/**
 * Employee Consent Flow Component
 *
 * Step-by-step consent flow for employees to:
 * 1. Review program information
 * 2. Review consent documents (HIPAA, Terms)
 * 3. Provide electronic signature
 * 4. Confirm consent acceptance
 *
 * Integrates with backend/routers/employee_consent.py
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { Progress } from '@/components/ui/progress';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import {
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  Loader2,
  FileText,
  Shield,
  PenTool,
  ChevronRight,
  ChevronLeft,
  Eye,
  Heart,
  Building2,
  User,
  Lock,
  Info,
  ExternalLink,
} from 'lucide-react';
import { apiClient } from '@/services/apiClient';

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

interface PendingOffer {
  consent_id: number;
  program_id: number;
  program_name: string;
  program_description?: string;
  employer_name: string;
  offered_at: string;
  expires_at?: string;
  services_included?: string[];
  consent_documents?: ConsentDocument[];
}

interface ConsentDocument {
  id: number;
  document_type: string;
  title: string;
  content_html?: string;
  version: string;
  effective_date: string;
  requires_signature: boolean;
}

interface EmployeeConsentFlowProps {
  employeeId?: number;
  offerId?: number;
  onConsentComplete?: (consentId: number) => void;
  onDecline?: () => void;
  className?: string;
}

type FlowStep = 'review_program' | 'review_documents' | 'sign_consent' | 'confirmation';

// ============================================================================
// COMPONENT
// ============================================================================

export function EmployeeConsentFlow({
  employeeId,
  offerId,
  onConsentComplete,
  onDecline,
  className = '',
}: EmployeeConsentFlowProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [pendingOffers, setPendingOffers] = useState<PendingOffer[]>([]);
  const [selectedOffer, setSelectedOffer] = useState<PendingOffer | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Flow state
  const [currentStep, setCurrentStep] = useState<FlowStep>('review_program');
  const [documentsRead, setDocumentsRead] = useState<Set<number>>(new Set());
  const [documentsAccepted, setDocumentsAccepted] = useState<Set<number>>(new Set());
  const [signatureName, setSignatureName] = useState('');
  const [signatureConfirmed, setSignatureConfirmed] = useState(false);
  const [consentResult, setConsentResult] = useState<any>(null);

  // Load pending offers
  const loadPendingOffers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiClient.get<PendingOffer[]>(
        '/api/employee-consent/offers/pending'
      );
      setPendingOffers(response.data);

      // If offerId provided, select that offer
      if (offerId) {
        const offer = response.data.find((o: PendingOffer) => o.consent_id === offerId);
        if (offer) setSelectedOffer(offer);
      } else if (response.data.length === 1) {
        setSelectedOffer(response.data[0]);
      }
    } catch (err) {
      console.error('Failed to load pending offers:', err);
      setError(t('serviceReferrals.consent.errors.loadFailed', 'Failed to load program offers'));
    } finally {
      setLoading(false);
    }
  }, [offerId]);

  useEffect(() => {
    loadPendingOffers();
  }, [loadPendingOffers]);

  // Handle consent acceptance
  const handleAcceptConsent = async () => {
    if (!selectedOffer || !signatureName) return;

    setSubmitting(true);
    setError(null);
    try {
      const response = await apiClient.post('/api/employee-consent/accept', {
        consent_id: selectedOffer.consent_id,
        signature_data: {
          name: signatureName,
          method: 'electronic',
          timestamp: new Date().toISOString(),
          ip_address: 'captured_server_side',
        },
      });

      setConsentResult(response.data);
      setCurrentStep('confirmation');
      onConsentComplete?.(selectedOffer.consent_id);
    } catch (err: any) {
      console.error('Failed to accept consent:', err);
      setError(err.response?.data?.detail || 'Failed to submit consent');
    } finally {
      setSubmitting(false);
    }
  };

  // Handle decline
  const handleDecline = async () => {
    if (!selectedOffer) return;

    setSubmitting(true);
    try {
      await apiClient.post('/api/employee-consent/decline', {
        consent_id: selectedOffer.consent_id,
        reason: 'Declined by employee',
      });
      onDecline?.();
    } catch (err) {
      console.error('Failed to decline consent:', err);
      setError(t('serviceReferrals.consent.errors.declineFailed', 'Failed to decline offer'));
    } finally {
      setSubmitting(false);
    }
  };

  // Mark document as read
  const handleDocumentRead = (docId: number) => {
    setDocumentsRead((prev) => new Set([...prev, docId]));
  };

  // Toggle document acceptance
  const handleDocumentAcceptance = (docId: number, accepted: boolean) => {
    setDocumentsAccepted((prev) => {
      const newSet = new Set(prev);
      if (accepted) {
        newSet.add(docId);
      } else {
        newSet.delete(docId);
      }
      return newSet;
    });
  };

  // Check if can proceed to next step
  const canProceed = () => {
    if (!selectedOffer) return false;

    switch (currentStep) {
      case 'review_program':
        return true;
      case 'review_documents':
        const allDocsAccepted = (selectedOffer.consent_documents || []).every(
          (doc) => documentsAccepted.has(doc.id)
        );
        return allDocsAccepted;
      case 'sign_consent':
        return signatureName.length >= 2 && signatureConfirmed;
      default:
        return false;
    }
  };

  // Get step progress
  const getStepProgress = () => {
    switch (currentStep) {
      case 'review_program':
        return 25;
      case 'review_documents':
        return 50;
      case 'sign_consent':
        return 75;
      case 'confirmation':
        return 100;
      default:
        return 0;
    }
  };

  // ========================================================================
  // RENDER STEPS
  // ========================================================================

  // Step 1: Review Program
  const renderProgramReview = () => (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <div className="p-3 rounded-lg bg-blue-100">
          <Building2 className="h-8 w-8 text-blue-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">{selectedOffer?.program_name}</h3>
          <p className="text-gray-600">Offered by {selectedOffer?.employer_name}</p>
        </div>
      </div>

      {selectedOffer?.program_description && (
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-gray-700">{selectedOffer.program_description}</p>
        </div>
      )}

      {selectedOffer?.services_included && selectedOffer.services_included.length > 0 && (
        <div>
          <h4 className="font-medium mb-2 flex items-center gap-2">
            <Heart className="h-4 w-4 text-red-500" />
            Services Included
          </h4>
          <ul className="space-y-2">
            {selectedOffer.services_included.map((service, idx) => (
              <li key={idx} className="flex items-center gap-2 text-gray-700">
                <CheckCircle className="h-4 w-4 text-green-500" />
                {service}
              </li>
            ))}
          </ul>
        </div>
      )}

      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription>
          By participating in this program, you will be connected with service providers
          who can help address your social and health needs. Your information is protected
          under HIPAA and our privacy policy.
        </AlertDescription>
      </Alert>
    </div>
  );

  // Step 2: Review Documents
  const renderDocumentReview = () => (
    <div className="space-y-6">
      <Alert className="bg-blue-50 border-blue-200">
        <FileText className="h-4 w-4 text-blue-600" />
        <AlertDescription className="text-blue-800">
          Please review and accept each document below to continue.
        </AlertDescription>
      </Alert>

      {(selectedOffer?.consent_documents || []).map((doc) => {
        const isRead = documentsRead.has(doc.id);
        const isAccepted = documentsAccepted.has(doc.id);

        return (
          <Card key={doc.id} className={isAccepted ? 'border-green-300 bg-green-50/30' : ''}>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  {doc.document_type === 'HIPAA' ? (
                    <Shield className="h-4 w-4 text-blue-600" />
                  ) : (
                    <FileText className="h-4 w-4 text-gray-600" />
                  )}
                  {doc.title}
                </CardTitle>
                <Badge variant="outline">v{doc.version}</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Document Preview */}
              <div className="bg-white border rounded-lg p-4 max-h-48 overflow-y-auto text-sm text-gray-600">
                {doc.content_html ? (
                  <div dangerouslySetInnerHTML={{ __html: doc.content_html }} />
                ) : (
                  <p>Document content will be displayed here...</p>
                )}
              </div>

              {/* View Full Document Link */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDocumentRead(doc.id)}
              >
                <Eye className="h-4 w-4 mr-2" />
                View Full Document
                <ExternalLink className="h-3 w-3 ml-1" />
              </Button>

              {/* Accept Checkbox */}
              <div className="flex items-start space-x-3 pt-2 border-t">
                <Checkbox
                  id={`accept-${doc.id}`}
                  checked={isAccepted}
                  onCheckedChange={(checked) => handleDocumentAcceptance(doc.id, checked as boolean)}
                />
                <div className="grid gap-1.5 leading-none">
                  <label
                    htmlFor={`accept-${doc.id}`}
                    className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                  >
                    I have read and agree to the {doc.title}
                  </label>
                  <p className="text-xs text-gray-500">
                    Effective date: {fmt.short(doc.effective_date)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );

  // Step 3: Sign Consent
  const renderSignConsent = () => (
    <div className="space-y-6">
      <Alert className="bg-amber-50 border-amber-200">
        <PenTool className="h-4 w-4 text-amber-600" />
        <AlertDescription className="text-amber-800">
          Please provide your electronic signature to complete the consent process.
        </AlertDescription>
      </Alert>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="signature-name">Type Your Full Legal Name</Label>
          <Input
            id="signature-name"
            placeholder="Enter your full name as signature"
            value={signatureName}
            onChange={(e) => setSignatureName(e.target.value)}
            className="text-lg"
          />
        </div>

        {signatureName && (
          <div className="p-4 bg-gray-50 border rounded-lg">
            <Label className="text-xs text-gray-500">Signature Preview</Label>
            <p className="font-['Brush_Script_MT',_cursive] text-3xl text-gray-800 mt-2">
              {signatureName}
            </p>
          </div>
        )}

        <div className="flex items-start space-x-3 pt-4 border-t">
          <Checkbox
            id="confirm-signature"
            checked={signatureConfirmed}
            onCheckedChange={(checked) => setSignatureConfirmed(checked as boolean)}
          />
          <div className="grid gap-1.5 leading-none">
            <label
              htmlFor="confirm-signature"
              className="text-sm font-medium leading-none"
            >
              I confirm that this is my electronic signature
            </label>
            <p className="text-xs text-gray-500">
              By checking this box, I agree that my electronic signature is the legal
              equivalent of my manual signature on this consent form.
            </p>
          </div>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-medium text-blue-900 mb-2">What happens next?</h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Your consent will be recorded and timestamped
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Service referrals may be created based on your needs
          </li>
          <li className="flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            Providers will be matched to help address your needs
          </li>
        </ul>
      </div>
    </div>
  );

  // Step 4: Confirmation
  const renderConfirmation = () => (
    <div className="text-center space-y-6">
      <div className="p-4 rounded-full bg-green-100 inline-block">
        <CheckCircle className="h-12 w-12 text-green-600" />
      </div>

      <div>
        <h3 className="text-xl font-semibold text-green-800">Consent Submitted Successfully!</h3>
        <p className="text-gray-600 mt-2">
          Thank you for completing the consent process for {selectedOffer?.program_name}.
        </p>
      </div>

      {consentResult?.referrals_created > 0 && (
        <Alert className="bg-blue-50 border-blue-200 text-left">
          <Heart className="h-4 w-4 text-blue-600" />
          <AlertDescription className="text-blue-800">
            <strong>{consentResult.referrals_created} service referral(s)</strong> have been
            created based on your needs. Providers will be matched shortly.
          </AlertDescription>
        </Alert>
      )}

      <div className="pt-4">
        <p className="text-sm text-gray-500">
          Consent ID: {selectedOffer?.consent_id} | Signed: {fmt.dateTime(new Date())}
        </p>
      </div>
    </div>
  );

  // ========================================================================
  // MAIN RENDER
  // ========================================================================

  if (loading) {
    return (
      <Card className={className}>
        <CardContent className="p-8 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
          <span className="ml-3 text-gray-500">Loading consent information...</span>
        </CardContent>
      </Card>
    );
  }

  if (!selectedOffer && pendingOffers.length === 0) {
    return (
      <Card className={className}>
        <CardContent className="p-8 text-center">
          <FileText className="h-12 w-12 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">{t('serviceReferrals.consent.noPendingOffers', 'No Pending Consent Offers')}</h3>
          <p className="text-gray-500">
            {t('serviceReferrals.consent.noPendingOffersDesc', "You don't have any pending program consent offers at this time.")}
          </p>
        </CardContent>
      </Card>
    );
  }

  // Show offer selection if multiple offers and none selected
  if (!selectedOffer && pendingOffers.length > 1) {
    return (
      <Card className={className}>
        <CardHeader>
          <CardTitle>Select a Program</CardTitle>
          <CardDescription>You have {pendingOffers.length} pending program offers</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {pendingOffers.map((offer) => (
            <Card
              key={offer.consent_id}
              className="cursor-pointer hover:border-blue-300 hover:bg-blue-50/30 transition-colors"
              onClick={() => setSelectedOffer(offer)}
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <h4 className="font-medium">{offer.program_name}</h4>
                  <p className="text-sm text-gray-500">{offer.employer_name}</p>
                </div>
                <ChevronRight className="h-5 w-5 text-gray-400" />
              </CardContent>
            </Card>
          ))}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader>
        <div className="flex items-center justify-between mb-4">
          <div>
            <CardTitle>{t('serviceReferrals.consent.title', 'Program Consent')}</CardTitle>
            <CardDescription>
              {currentStep === 'confirmation'
                ? 'Consent completed'
                : 'Complete the steps below to accept the program offer'}
            </CardDescription>
          </div>
          {currentStep !== 'confirmation' && (
            <Badge variant="outline">
              Step {currentStep === 'review_program' ? 1 : currentStep === 'review_documents' ? 2 : 3} of 3
            </Badge>
          )}
        </div>

        {/* Progress Bar */}
        <Progress value={getStepProgress()} className="h-2" />

        {/* Step Indicators */}
        {currentStep !== 'confirmation' && (
          <div className="flex justify-between mt-2 text-xs text-gray-500">
            <span className={currentStep === 'review_program' ? 'text-blue-600 font-medium' : ''}>
              Review Program
            </span>
            <span className={currentStep === 'review_documents' ? 'text-blue-600 font-medium' : ''}>
              Review Documents
            </span>
            <span className={currentStep === 'sign_consent' ? 'text-blue-600 font-medium' : ''}>
              Sign Consent
            </span>
          </div>
        )}
      </CardHeader>

      <CardContent className="pt-0">
        {error && (
          <Alert variant="destructive" className="mb-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {currentStep === 'review_program' && renderProgramReview()}
        {currentStep === 'review_documents' && renderDocumentReview()}
        {currentStep === 'sign_consent' && renderSignConsent()}
        {currentStep === 'confirmation' && renderConfirmation()}
      </CardContent>

      {currentStep !== 'confirmation' && (
        <CardFooter className="flex justify-between border-t pt-4">
          <div>
            {currentStep !== 'review_program' && (
              <Button
                variant="outline"
                onClick={() => {
                  if (currentStep === 'review_documents') setCurrentStep('review_program');
                  if (currentStep === 'sign_consent') setCurrentStep('review_documents');
                }}
              >
                <ChevronLeft className="h-4 w-4 mr-2" />
                Back
              </Button>
            )}
          </div>

          <div className="flex gap-2">
            <Button variant="ghost" onClick={handleDecline} disabled={submitting}>
              {t('serviceReferrals.consent.decline', 'Decline')}
            </Button>

            {currentStep === 'sign_consent' ? (
              <Button
                onClick={handleAcceptConsent}
                disabled={!canProceed() || submitting}
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <PenTool className="h-4 w-4 mr-2" />
                )}
                {t('serviceReferrals.consent.submit', 'Submit Consent')}
              </Button>
            ) : (
              <Button
                onClick={() => {
                  if (currentStep === 'review_program') setCurrentStep('review_documents');
                  if (currentStep === 'review_documents') setCurrentStep('sign_consent');
                }}
                disabled={!canProceed()}
              >
                {t('serviceReferrals.consent.continue', 'Continue')}
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            )}
          </div>
        </CardFooter>
      )}
    </Card>
  );
}

export default EmployeeConsentFlow;
